import React, { useState, useRef, useEffect } from "react";
import theme from "../data/theme";
import { FORMAT_PRESETS, presetSubtitle } from "./SettingsModal";
import LeagueChip, { CalibratedChip } from "./LeagueChip";
import { leagueFormatLabel } from "../utils/leagueFormat";

// The chip text for each format label. The CLASSIFICATION lives in
// leagueFormatLabel so the header, the analytics dimension and the engine's own
// QB-premium predicate can never disagree about what a roster is; this map only
// decides how it is spelled to a user.
const FORMAT_CHIP = { sflex: "SUPERFLEX", "2qb": "2QB", "1qb": "1QB" };

const Header = React.memo(function Header({ appMode, setAppMode, mockDraftActive, setMockLeaveTarget, settings, onLogoClick, bottomContent, children, savedLeagues, activeLeagueName, onLoadLeague, onOpenSettings, onCreateLeague, onEditLeague, onOpenAlmanac, leagueLocked = false, calibrated = false, liveBoardEnabled = false }) {
  const [leagueOpen, setLeagueOpen] = useState(false);
  const leagueRef = useRef(null);

  // A live draft is built from the active league's settings and opponent configs,
  // so switching mid-draft would leave the auction running against a roster/budget
  // shape it wasn't built for. Lock the selector shut until the draft ends.
  useEffect(() => { if (leagueLocked) setLeagueOpen(false); }, [leagueLocked]);

  useEffect(() => {
    if (!leagueOpen) return;
    const handleClick = (e) => {
      if (leagueRef.current && !leagueRef.current.contains(e.target)) setLeagueOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [leagueOpen]);
  const summaryParts = [];
  // PPR type
  const rec = settings.scoring.receptions;
  summaryParts.push(rec === 0 ? "NON-PPR" : rec === 0.5 ? "HALF-PPR" : rec === 1 ? "FULL PPR" : `${rec} PPR`);
  // Pass TD (skip if standard 4pt)
  summaryParts.push(`${settings.scoring.passTD}PT PASS TD`);
  // TE Premium
  if (settings.scoring.recBonusTE > 0) summaryParts.push("TE PREMIUM");
  // QB format. Three cases, not two — a 2QB league was reading "1QB", which is
  // the same mis-detection that put it on the 1QB price curve. Order matters:
  // SFLEX wins when a roster carries both (3 such rooms in the Aug-13 corpus),
  // because the superflex slot is the more permissive of the two — which is
  // exactly the ordering leagueFormatLabel encodes, so this reads it rather
  // than keeping a second copy of the same ternary.
  const pos = settings.positions;
  summaryParts.push(FORMAT_CHIP[leagueFormatLabel(pos)]);
  // No K / No DEF
  if (pos.K === 0) summaryParts.push("NO K");
  if (pos.DEF === 0) summaryParts.push("NO DEF");
  // Budget and teams
  summaryParts.push(`$${settings.budget}`);
  summaryParts.push(`${settings.teams} TEAMS`);
  const settingsSummary = summaryParts.join(" • ");

  // The league selector renders only when there is something to select; the
  // summary's LeagueChip always renders. Both read the same activeLeagueName
  // prop with the same "DEFAULT" fallback, so they can never disagree — they
  // were simply printing it twice. De-duplicate toward the badge, but only when
  // the badge is actually on screen, or a user with no saved leagues would see
  // the name zero times instead of once. Draft-only: every other screen keeps
  // today's two-row header untouched.
  const leagueNameInBadge = mockDraftActive
    && !!(savedLeagues && savedLeagues.length > 0 && onLoadLeague);

  return (
    <div className={`app-header${mockDraftActive ? " mock-drafting-header" : ""}`}>
      <div className="header-inner">
        <div className="header-flex" data-testid="header-flex">
          <div className={`header-title${mockDraftActive ? " mock-drafting" : ""}`} data-testid="header-title">
            {/* Sized for a ONE-LINE header: the logo sits beside the tabs, not
                above them. All three lines of the mark are kept — dropping to a
                bare wordmark would lose "AUCTION SUITE / POWERED BY REAL
                AUCTION DATA" from every page to save ~10px. */}
            <svg onClick={onLogoClick} viewBox="0 0 300 78" style={{ width: 186, height: 48, display: "block", cursor: "pointer", flexShrink: 0 }} xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="m1grad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={theme.colors.brand.orange} />
                  <stop offset="100%" stopColor={theme.colors.brand.accent} />
                </linearGradient>
                <linearGradient id="m1line" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={theme.colors.brand.accent} stopOpacity="0.7" />
                  <stop offset="100%" stopColor={theme.colors.brand.accent} stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* Speed lines trailing right of the "1" */}
              <rect x="168" y="6"  width="55" height="2.5" fill="url(#m1line)" opacity="0.5" rx="1" />
              <rect x="168" y="14" width="60" height="2.5" fill="url(#m1line)" opacity="0.8" rx="1" />
              <rect x="168" y="22" width="50" height="2"   fill="url(#m1line)" opacity="0.6" rx="1" />
              <rect x="168" y="29" width="45" height="2"   fill="url(#m1line)" opacity="0.4" rx="1" />
              <rect x="168" y="36" width="55" height="2.5" fill="url(#m1line)" opacity="0.5" rx="1" />
              <text y="38" fontFamily="'Outfit', sans-serif" fontWeight="900" fontSize="44" letterSpacing="-1">
                <tspan fill="#ffffff">MOCK</tspan><tspan fill="url(#m1grad)"> 1</tspan>
              </text>
              <text x="2" y="55" fontFamily="'Outfit', sans-serif" fontWeight="600" fontSize="12" fill="#ffffff" letterSpacing="4.5" opacity="0.9">AUCTION SUITE</text>
              <text x="2" y="71" fontFamily="'Outfit', sans-serif" fontWeight="500" fontSize="8.5" fill={theme.colors.text.secondary} letterSpacing="1.5">POWERED BY REAL AUCTION DATA</text>
            </svg>
            <div className="mode-tabs" data-testid="mode-tabs">
              <span className="mobile-logo" onClick={onLogoClick} style={{ cursor: "pointer" }}>
                <svg viewBox="0 0 82 22" style={{ width: 72, height: 20, display: "block" }} xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="m1gradM" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={theme.colors.brand.orange} /><stop offset="100%" stopColor={theme.colors.brand.accent} />
                    </linearGradient>
                    <linearGradient id="m1lineM" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={theme.colors.brand.accent} stopOpacity="1" /><stop offset="100%" stopColor={theme.colors.brand.accent} stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <rect x="66" y="2" width="18" height="1.5" fill="url(#m1lineM)" opacity="0.55" rx="0.75" />
                  <rect x="66" y="6" width="22" height="1.5" fill="url(#m1lineM)" opacity="0.8" rx="0.75" />
                  <rect x="66" y="10" width="16" height="1.2" fill="url(#m1lineM)" opacity="0.65" rx="0.6" />
                  <rect x="66" y="13.5" width="14" height="1.2" fill="url(#m1lineM)" opacity="0.45" rx="0.6" />
                  <rect x="66" y="17" width="18" height="1.5" fill="url(#m1lineM)" opacity="0.55" rx="0.75" />
                  <text y="17" fontFamily="'Outfit', sans-serif" fontWeight="900" fontSize="18" letterSpacing="-0.5">
                    <tspan fill="#ffffff">MOCK</tspan><tspan fill="url(#m1gradM)"> 1</tspan>
                  </text>
                </svg>
              </span>
              {/* Tiers and Team Builder navigate straight through, even mid-draft.
                  setAppMode alone does NOT destroy a draft — resetMockDraft(), called
                  only from the leave-confirm's Leave handler, is the sole destructive
                  path. The Almanac route already proves it: AuctionDraftBuilder forces
                  mockDraftActive false on special pages, so Almanac -> Tiers has always
                  taken this unguarded branch and the draft resumes intact. The MOCK tab
                  below has likewise always been unconditional. The logo keeps its
                  confirm, because leaving to the landing page is a different intent. */}
              <button className="mode-tab-btn" onClick={() => setAppMode("builder")} style={{
                background: appMode === "builder" ? theme.colors.brand.orange : "transparent",
                color: appMode === "builder" ? "#fff" : theme.colors.text.muted,
              }}><span className="tab-long">TEAM BUILDER</span><span className="tab-short">BUILDER</span></button>
              {/* Draft Plan ships as a beta alongside the Live Board, and wears
                  the badge the Almanac and the Live Board already wear. Same
                  reasoning as the note below the MOCK tab: three betas spelled
                  three ways would read as three different promises. */}
              <button className="mode-tab-btn" onClick={() => setAppMode("plan")} style={{
                background: appMode === "plan" ? theme.colors.brand.orange : "transparent",
                color: appMode === "plan" ? "#fff" : theme.colors.text.muted,
                display: "inline-flex", alignItems: "center", gap: 5,
              }}>
                <span className="tab-long">DRAFT PLAN</span><span className="tab-short">PLAN</span>
                <span className="nav-beta-badge" style={{
                  fontSize: theme.fontSize.nano, fontWeight: theme.fontWeight.heavy,
                  letterSpacing: "0.5px", color: theme.colors.brand.orange,
                  background: theme.helpers.brandBg(0.12), border: `1px solid ${theme.colors.border.brandLight}`,
                  borderRadius: 4, padding: "1px 4px", lineHeight: 1,
                }}>BETA</span>
              </button>
              <button className="mode-tab-btn" onClick={() => setAppMode("tiers")} style={{
                background: appMode === "tiers" ? theme.colors.brand.orange : "transparent",
                color: appMode === "tiers" ? "#fff" : theme.colors.text.muted,
              }}>TIERS</button>
              <button className="mode-tab-btn" onClick={() => setAppMode("mock")} style={{
                background: appMode === "mock" ? theme.colors.brand.gradientBtn : "transparent",
                color: appMode === "mock" ? "#fff" : theme.colors.text.muted,
              }}><span className="tab-long">MOCK DRAFT</span><span className="tab-short">MOCK</span></button>
              {/* Live Board is a real-auction surface, not a simulation: it
                  shares no state with the mock draft and is safe to open while
                  one is parked, exactly like the tabs above.
                  PUBLIC BETA since Aug 26 — liveBoardFlag.js is default-on and
                  `?live=0` is the way back out. The badge is the Almanac's, not
                  a new component: two betas spelled two ways would read as two
                  different promises. The shared /watch page is NOT gated: it
                  never renders this header. */}
              {liveBoardEnabled && (
                <button className="mode-tab-btn" onClick={() => setAppMode("live")} style={{
                  background: appMode === "live" ? theme.colors.brand.orange : "transparent",
                  color: appMode === "live" ? "#fff" : theme.colors.text.muted,
                  display: "inline-flex", alignItems: "center", gap: 5,
                }}>
                  <span className="tab-long">LIVE BOARD</span><span className="tab-short">LIVE</span>
                  <span className="nav-beta-badge" style={{
                    fontSize: theme.fontSize.nano, fontWeight: theme.fontWeight.heavy,
                    letterSpacing: "0.5px", color: theme.colors.brand.orange,
                    background: theme.helpers.brandBg(0.12), border: `1px solid ${theme.colors.border.brandLight}`,
                    borderRadius: 4, padding: "1px 4px", lineHeight: 1,
                  }}>BETA</span>
                </button>
              )}
              {onOpenAlmanac && (
                <button className="mode-tab-btn" onClick={onOpenAlmanac} style={{
                  background: "transparent", color: theme.colors.text.muted,
                  display: "inline-flex", alignItems: "center", gap: 5,
                }}>
                  ALMANAC
                  <span className="nav-beta-badge" style={{
                    fontSize: theme.fontSize.nano, fontWeight: theme.fontWeight.heavy,
                    letterSpacing: "0.5px", color: theme.colors.brand.orange,
                    background: theme.helpers.brandBg(0.12), border: `1px solid ${theme.colors.border.brandLight}`,
                    borderRadius: 4, padding: "1px 4px", lineHeight: 1,
                  }}>BETA</span>
                </button>
              )}
            </div>
          </div>
          {children}
            {/* League selector — IN FLOW, as a flex child of .header-flex.
                🔴 IT USED TO BE position:absolute, pinned to the top-right of
                .header-inner. Out of flow it could not see the mode's own control
                cluster ({children}, the .header-row carrying Build name / Save /
                Builds / Settings / Clear on Team Builder) — it simply painted on
                top of it, and the only thing that kept them apart was the header
                being three rows tall. Collapsing to one row removed that
                clearance and they collided.
                Two `top` anchors that switched on whether a SIBLING had rendered
                was the fragile part; tuning the offset would only move the
                collision to a different width. As a flex child it cannot overlap
                a flex sibling at any width, and it wraps with them instead. */}
          {savedLeagues && savedLeagues.length > 0 && onLoadLeague && (
            <div ref={leagueRef} className="header-league-selector" data-testid="header-league-selector" style={{ position: "relative", zIndex: 1000, textAlign: "right" }}>
              <button
                className="hls-btn"
                onClick={() => { if (!leagueLocked) setLeagueOpen(v => !v); }}
                disabled={leagueLocked}
                title={leagueLocked ? "Finish or exit this draft to switch leagues" : undefined}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "4px 12px", borderRadius: 8,
                  background: leagueLocked ? "rgba(255,255,255,0.04)" : "rgba(255,107,0,0.1)",
                  border: `1px solid ${leagueLocked ? theme.colors.border.subtle : "rgba(255,107,0,0.5)"}`,
                  color: leagueLocked ? theme.colors.text.muted : theme.colors.brand.accent,
                  fontSize: 12, fontWeight: 700,
                  fontFamily: theme.fonts.body, whiteSpace: "nowrap",
                  cursor: leagueLocked ? "not-allowed" : "pointer",
                  opacity: leagueLocked ? 0.55 : 1,
                  boxShadow: leagueLocked ? "none" : "0 0 12px rgba(255,107,0,0.15)",
                }}>
                {/* Name is the hero \u2014 the tiny LEAGUE kicker sits above it. */}
                <span className="hls-stack" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1, lineHeight: 1.1 }}>
                  <span className="hls-kicker" style={{ fontSize: 10, fontWeight: 800, color: leagueLocked ? theme.colors.text.faint : theme.colors.brand.orange, letterSpacing: "1px", opacity: 0.7 }}>LEAGUE</span>
                  <span className="hls-name" style={{
                    fontSize: 14, fontWeight: 700,
                    color: leagueLocked ? theme.colors.text.muted
                      : activeLeagueName ? theme.colors.brand.accent : theme.colors.text.muted,
                  }}>{activeLeagueName || "DEFAULT"}</span>
                </span>
                <span className="hls-mark" style={{ fontSize: 9, color: leagueLocked ? theme.colors.text.faint : theme.colors.brand.orange }}>{leagueLocked ? "\u{1F512}" : leagueOpen ? "\u25B2" : "\u25BC"}</span>
              </button>
              {leagueOpen && !leagueLocked && (
                <div style={{
                  position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 1001,
                  minWidth: 180, padding: 4, borderRadius: 10,
                  background: theme.colors.bg.surface, border: `1px solid ${theme.colors.border.subtle}`,
                  boxShadow: "0 4px 20px rgba(255,107,0,0.3)", animation: "fadeInUp 0.15s ease-out",
                }}>
                  {/* FIRST, above everything. This dropdown is where a user
                      goes when they want to be in a different league, and for a
                      new user the league they want does not exist yet — so the
                      way to make one has to be the first thing in the list, not
                      a line of small text on a tab inside a modal. */}
                  {onCreateLeague && (
                    <>
                      <div onClick={() => { onCreateLeague(); setLeagueOpen(false); }} style={{
                        padding: "8px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700,
                        color: theme.colors.brand.orange, cursor: "pointer", transition: "background 0.1s ease",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,107,0,0.08)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >+ Create new league</div>
                      <div style={{ borderTop: `1px solid ${theme.colors.border.subtle}`, margin: "4px 8px" }} />
                    </>
                  )}
                  <div style={{ padding: "4px 12px 2px", fontSize: 9, fontWeight: 700, color: theme.colors.text.faint, letterSpacing: "1px" }}>SAVED</div>
                  {savedLeagues.map(league => {
                    const isActive = activeLeagueName === league.name;
                    return (
                      <div key={league.name} style={{
                        display: "flex", alignItems: "center", gap: 4,
                        borderRadius: 6,
                        background: isActive ? "rgba(255,107,0,0.08)" : "transparent",
                        transition: "background 0.1s ease",
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                      >
                        {/* The name still fills the row and still selects on
                            click — flex:1 means the pencil takes width from the
                            padding, not from the target you were already aiming
                            at. Selecting a league must not get harder because
                            editing it got easier. */}
                        <div onClick={() => { if (!isActive) onLoadLeague(league); setLeagueOpen(false); }} style={{
                          flex: 1, minWidth: 0, padding: "8px 4px 8px 12px",
                          fontSize: 12, fontWeight: isActive ? 700 : 500,
                          color: isActive ? theme.colors.brand.orange : theme.colors.text.primary,
                          cursor: isActive ? "default" : "pointer",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>{league.name}</div>
                        {/* 40×36 hit target on a 14px glyph — the icon is small
                            because it is secondary, the target is not, because
                            thumbs are not. */}
                        {onEditLeague && (
                          <button
                            onClick={e => { e.stopPropagation(); onEditLeague(league); setLeagueOpen(false); }}
                            title={`Edit ${league.name}`}
                            aria-label={`Edit ${league.name}`}
                            style={{
                              flexShrink: 0, width: 40, height: 36, padding: 0,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              background: "transparent", border: "none", borderRadius: 6,
                              color: theme.colors.text.muted, fontSize: 13, cursor: "pointer",
                              fontFamily: "inherit", lineHeight: 1,
                            }}
                            onMouseEnter={e => { e.currentTarget.style.color = theme.colors.brand.orange; e.currentTarget.style.background = "rgba(255,107,0,0.12)"; }}
                            onMouseLeave={e => { e.currentTarget.style.color = theme.colors.text.muted; e.currentTarget.style.background = "transparent"; }}
                          >{"✎"}</button>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ borderTop: `1px solid ${theme.colors.border.subtle}`, margin: "4px 8px" }} />
                  <div style={{ padding: "4px 12px 2px", fontSize: 9, fontWeight: 700, color: theme.colors.text.faint, letterSpacing: "1px" }}>START FROM A PRESET</div>
                  {FORMAT_PRESETS.map(preset => (
                    <div key={preset.name} onClick={() => { onLoadLeague(preset); setLeagueOpen(false); }} style={{
                      padding: "8px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500,
                      color: theme.colors.brand.deep, cursor: "pointer", transition: "background 0.1s ease",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      {preset.name}
                      {/* Same presetSubtitle the modal buttons use — imported, not
                          restated, so the two lists can never describe the same
                          preset differently. Picking a format from here is the
                          blindest choice in the app; it gets the identical spec. */}
                      <div style={{ fontSize: 9, fontWeight: 600, opacity: 0.7, letterSpacing: "0.2px", marginTop: 2 }}>
                        {presetSubtitle(preset)}
                      </div>
                    </div>
                  ))}
                  {onOpenSettings && (
                    <>
                      <div style={{ borderTop: `1px solid ${theme.colors.border.subtle}`, margin: "4px 8px" }} />
                      <div onClick={() => { onOpenSettings(); setLeagueOpen(false); }} style={{
                        padding: "8px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                        color: theme.colors.brand.orange, cursor: "pointer", transition: "background 0.1s ease",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,107,0,0.08)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >+ Import League</div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Settings summary — own row, fixed height to prevent layout shift.
            SUPPRESSED ON THE LIVE BOARD: that page already prints
            "<league> · $200 · 12 teams · N sold" above the roster grid, so this
            row is the same four facts a second time, and the Live Board is the
            one surface where vertical space is the scarce resource. Every other
            surface keeps it. */}
        {appMode !== "live" && (
        <div className="header-summary-row" data-testid="header-summary-row" style={{
          height: 20, display: "flex", alignItems: "center", justifyContent: "space-between",
          overflow: "hidden", marginTop: 2,
        }}>
          {/* Active league last — settings now carry per-league state (opponent
              configs, tier overrides), so the summary has to say WHICH one. */}
          {/* The calibration badge lives INSIDE this <p>, not beside it, and
              that placement is the whole gate story:
                - desktop: the row is a fixed height:20 / overflow:hidden line,
                  so adding a chip inside it cannot change header height, and
                  no chip means no shift.
                - mobile in a live draft: auction.css hides
                  `.mock-drafting-header .header-subtitle` outright, so the
                  badge disappears with its parent. A sibling of this <p> would
                  instead survive that rule and give the header ~18px back on
                  the single most vertically-starved view.
              Inheriting the existing gates beats writing new ones. */}
          <p className="header-subtitle" style={{ margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {settingsSummary}
            {/* Suppressed mid-draft when the top-right badge is carrying the
                name — see leagueNameInBadge above. */}
            {!leagueNameInBadge && <>{" • "}<LeagueChip name={activeLeagueName} /></>}
            {/* Draft-time only. The calibrate toggle lives on the mock setup
                screen, which is unreachable once a draft is live, so this can't
                go stale mid-auction. */}
            {mockDraftActive && calibrated && <CalibratedChip name={activeLeagueName} />}
          </p>
        </div>
        )}

        {bottomContent}
      </div>
    </div>
  );
});

export default Header;
